import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type ReactElement,
} from "react";
import { Dialog } from "@base-ui/react/dialog";
import { useNavigate } from "@tanstack/react-router";
import { FiFileText, FiGlobe, FiSearch, FiUpload } from "react-icons/fi";
import { useSearch, type SearchResultItem } from "../hooks/useSearch";

interface SearchModalProps {
	open: boolean;
	onClose: () => void;
}

const TYPE_ICONS: Record<string, typeof FiGlobe> = {
	content: FiGlobe,
	document: FiFileText,
	upload: FiUpload,
};

const TYPE_LABELS: Record<string, string> = {
	content: "URL",
	document: "Document",
	upload: "Upload",
};

export function SearchModal({ open, onClose }: SearchModalProps): ReactElement {
	const [query, setQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState("");
	const [activeIndex, setActiveIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	// Debounce query
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQuery(query), 300);
		return () => clearTimeout(timer);
	}, [query]);

	const { data: results, isFetching } = useSearch(debouncedQuery);
	const items = results ?? [];

	// Reset active index when results change — adjust during render instead of useEffect
	const [prevDebouncedQuery, setPrevDebouncedQuery] =
		useState(debouncedQuery);
	if (debouncedQuery !== prevDebouncedQuery) {
		setPrevDebouncedQuery(debouncedQuery);
		setActiveIndex(0);
	}

	const navigateToResult = useCallback(
		(item: SearchResultItem) => {
			onClose();
			if (item.type === "document" && item.documentId) {
				navigate({
					to: "/doc/$docId",
					params: { docId: item.documentId },
					search: {
						highlight: query,
						chunkIndex: item.matchedChunkIndex ?? undefined,
					},
				});
			} else if (item.contentId) {
				navigate({
					to: "/content/$contentId",
					params: { contentId: item.contentId },
					search: {
						highlight: query,
						chunkIndex: item.matchedChunkIndex ?? undefined,
					},
				});
			}
		},
		[navigate, onClose, query],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "ArrowDown") {
				e.preventDefault();
				setActiveIndex((i) => Math.min(i + 1, items.length - 1));
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				setActiveIndex((i) => Math.max(i - 1, 0));
			} else if (e.key === "Enter" && items[activeIndex]) {
				e.preventDefault();
				navigateToResult(items[activeIndex]);
			}
		},
		[items, activeIndex, navigateToResult],
	);

	// Scroll active item into view
	useEffect(() => {
		const list = listRef.current;
		if (!list) return;
		const activeEl = list.children[activeIndex] as HTMLElement | undefined;
		activeEl?.scrollIntoView({ block: "nearest" });
	}, [activeIndex]);

	const showResults = debouncedQuery.trim().length >= 2;
	const showEmpty = showResults && !isFetching && items.length === 0;

	return (
		<Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
			<Dialog.Portal>
				<Dialog.Backdrop className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm" />
				<Dialog.Popup className="bg-surface border-line fixed top-[15vh] left-1/2 z-[101] w-full max-w-[560px] -translate-x-1/2 overflow-hidden rounded-xl border shadow-2xl">
					<div className="border-line flex items-center gap-3 border-b px-4 py-3">
						<FiSearch className="text-fg-ghost shrink-0 text-lg" />
						<input
							ref={inputRef}
							type="text"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder="Search your library..."
							autoFocus
							className="text-fg flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--color-fg-ghost)]"
						/>
						{isFetching && (
							<div className="border-accent h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-t-transparent" />
						)}
						<kbd className="text-fg-ghost border-line-strong hidden rounded border px-1.5 py-0.5 text-[10px] font-light sm:inline">
							ESC
						</kbd>
					</div>

					{showResults && items.length > 0 && (
						<div
							ref={listRef}
							className="max-h-[50vh] overflow-y-auto py-2"
						>
							{items.map((item, i) => {
								const Icon = TYPE_ICONS[item.type] ?? FiGlobe;
								const label =
									item.type === "content"
										? (item.domain ?? "URL")
										: (TYPE_LABELS[item.type] ?? item.type);
								return (
									<button
										key={item.libraryItemId}
										type="button"
										onClick={() => navigateToResult(item)}
										onMouseEnter={() => setActiveIndex(i)}
										className={`flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors ${
											i === activeIndex
												? "bg-surface-raised"
												: ""
										}`}
									>
										<Icon className="text-fg-ghost mt-0.5 shrink-0" />
										<div className="min-w-0 flex-1">
											<div className="text-fg-strong truncate text-sm">
												{item.title}
											</div>
											<div className="text-fg-ghost mt-0.5 flex items-center gap-2 text-xs">
												<span className="text-accent-muted">
													{label}
												</span>
												{item.summary && (
													<span className="truncate">
														{item.summary}
													</span>
												)}
											</div>
										</div>
									</button>
								);
							})}
						</div>
					)}

					{showEmpty && (
						<div className="text-fg-ghost px-4 py-8 text-center text-sm">
							No results for "{debouncedQuery}"
						</div>
					)}

					{!showResults && (
						<div className="text-fg-ghost px-4 py-6 text-center text-sm">
							Type to search your library
						</div>
					)}
				</Dialog.Popup>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
